//#region IMPORTS
import { promosSelectors } from "../../features/promos/promosSlice";
import { useAppSelector } from "../../store/configureStore";
import { useHistory } from "react-router-dom";
import { Promo } from "../../models/promo";
import useActiveWeekInstoreTemplates from "../../hooks/useActiveWeekInstoreTemplates";
import useActiveWeekInstoreTemplateForStore from "../../hooks/useActiveWeekInstoreTemplateForStore";
import useBasketGate from "../../hooks/useBasketGate";
import PromosPageStaticItem from "./PromosPageStaticItem";
import PromosPageItem from "./PromosPageItem";
//#endregion

//#region INTERFACE
interface Props {
  overWriteTransSmartHandler: (promo: Promo) => void;
  refreshTransmartHandler: (promo: Promo) => void;
  setProjectToDelete: (promo: Promo) => void;
  setProjectToClear: (promo: Promo) => void;
  setProjectToEdit: (promo: Promo, uploadData: boolean) => void;
  isPicker: boolean;
  isAdmin: boolean;
  hqTeam: boolean;
}
//#endregion

export default function PromosPageItems({
  overWriteTransSmartHandler,
  refreshTransmartHandler,
  setProjectToDelete,
  setProjectToClear,
  setProjectToEdit,
  isPicker,
  isAdmin,
  hqTeam,
}: Props) {
  //#region SETUP
  // Router
  const history = useHistory();

  // Redux
  const { basket } = useAppSelector((state) => state.basket);
  const { user } = useAppSelector((state) => state.account);
  const promos = useAppSelector(promosSelectors.selectAll);
  const currentPromo = useAppSelector(
    (state) => basket && promosSelectors.selectById(state, basket.promo_id)
  );

  // Active "Week" instore templates within today's date window, one per
  // instore type.
  const activeWeekTemplates = useActiveWeekInstoreTemplates();

  // The week template available for the current user's own store format
  // (store format → instore type → Week template).
  const storeWeekTemplate = useActiveWeekInstoreTemplateForStore(
    user?.store?.store_format_id
  );

  // HQ / Picker / Admin see every instore type's week template. A regular
  // store user only sees the one available for their store format.
  const seesAllTypes = hqTeam || isPicker || isAdmin;
  const visibleWeekTemplates = seesAllTypes
    ? activeWeekTemplates
    : storeWeekTemplate
      ? [storeWeekTemplate]
      : [];

  // Cross-basket gate: block week-order navigation when promo basket is active
  const { guardInstoreAction, promoActive } = useBasketGate();
  //#endregion

  //#region RENDER
  return (
    <ul className="divide-y divide-gray-100">
      {visibleWeekTemplates.map((template) => (
        <PromosPageStaticItem
          key={template.id}
          template={template}
          isAdmin={isAdmin}
          hqTeam={hqTeam}
          viewHandler={() => history.push(`/promos/instore/${template.id}`)}
          editHandler={() =>
            history.push(`/instoretemplates/${template.id}`)
          }
          orderHandler={guardInstoreAction(() =>
            history.push(`/promos/instore/${template.id}`),
          )}
          orderDisabled={promoActive}
        />
      ))}
      {promos.map((promo) => (
        <PromosPageItem
          overWriteTransSmartHandler={() => overWriteTransSmartHandler(promo)}
          refreshTransmartHandler={() => refreshTransmartHandler(promo)}
          setProjectToDelete={() => setProjectToDelete(promo)}
          setProjectToClear={() => setProjectToClear(promo)}
          setProjectToEdit={() => setProjectToEdit(promo, false)}
          promoInBasket={currentPromo}
          uploadHandler={() => setProjectToEdit(promo, true)}
          isPicker={isPicker}
          isAdmin={isAdmin}
          hqTeam={hqTeam}
          promo={promo}
          key={promo.id}
        />
      ))}
    </ul>
  );
  //#endregion
}
